/* eslint-disable react/style-prop-object */
import { useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Typography from '@alltrails/shared/denali/components/Typography';
import Link from '@alltrails/shared/denali/components/Link';
import useUser from 'hooks/useUser';
import PurchaseTier from 'types/PurchaseTier';
import useIsMobileSizedScreen from 'components/NavigationHeader/useIsMobileSizedScreen';
import { SPACE_300 } from '@alltrails/shared/denali/tokens';
import useExperiments from 'hooks/useExperiments';
import useExposureEvent from 'hooks/useExposureEvent';
import * as styles from './PlanSelection.module.scss';
import PlusLogoBadge from '../SubscriptionBadge/Logo/Plus';
import PeakLogoBadge from '../SubscriptionBadge/Logo/Peak';
import getPricingText from './getPricingText';
import TrialReminder from './TrialReminder';

// Figma file: https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5541-138083&t=JJQ3VDYh6Mc0P7Ol-0

type Props = {
  tierSelected: PurchaseTier;
  setTierSelected: (tier: PurchaseTier) => void;
  hideCompareLink?: boolean;
  className?: string;
  showPlusFirst?: boolean;
};

const PlanSelection = ({ tierSelected, setTierSelected, hideCompareLink = false, className, showPlusFirst = false }: Props) => {
  const { currencyCode, plans } = __AT_DATA__;
  const user = useUser();
  const isMobileSizedScreen = useIsMobileSizedScreen();
  const isPromoEligible = user?.isPromoEligible;
  const experiments = useExperiments();
  const isConditionsPlusEnabled = experiments['rm-conditions-plus']?.value === 'on';
  const isTrialTimelineTreatmentEnabled = experiments['web-growth-rm-trial-timeline']?.value === 'treatment' && isPromoEligible;
  const sendExposure = useExposureEvent('web-growth-rm-trial-timeline');

  useEffect(() => {
    if (isPromoEligible) {
      sendExposure();
    }
  }, [sendExposure, isPromoEligible]);

  const getPeakText = () => {
    if (isConditionsPlusEnabled) {
      return <FormattedMessage defaultMessage="Custom routes, plant identifier, and more" />;
    }
    return isMobileSizedScreen ? (
      <FormattedMessage defaultMessage="Custom routes, plant identifier, and more" />
    ) : (
      <FormattedMessage defaultMessage="Plant identifier, community heatmaps, and more" />
    );
  };

  const scrollToFeatureList = () => {
    const featureListContainer = document.querySelector('#feature-list');
    const modalScrollContainer = document.querySelector('#modal-scroll-container');

    const modalContainer = document.querySelector('#modal');

    if (featureListContainer && modalScrollContainer && modalContainer?.firstElementChild) {
      const modalHeaderHeight = (modalContainer.firstElementChild as HTMLElement).offsetHeight;
      const featureListPosition = (featureListContainer as HTMLElement).offsetTop - SPACE_300; // space-300 for scroll margin

      modalScrollContainer.scrollTo({
        top: featureListPosition - modalHeaderHeight,
        behavior: 'smooth'
      });
    }
  };

  const plusPlanButton = (
    <button
      type="button"
      className={classNames(styles.planContainer, { [styles.plusSelected]: tierSelected === 'plus' })}
      onClick={() => {
        setTierSelected('plus');
      }}
    >
      <PlusLogoBadge size="md" variant="full" />
      <Typography variant="text200" className={styles.description}>
        {isConditionsPlusEnabled ? (
          <FormattedMessage defaultMessage="Offline maps, trail conditions, and more" />
        ) : (
          <FormattedMessage defaultMessage="Offline maps, trail previews, and more" />
        )}
      </Typography>
      <Typography variant="text100">{getPricingText({ plan: plans.plus, currencyCode, isPromoEligible, isMobileSizedScreen })}</Typography>
      {isTrialTimelineTreatmentEnabled && (
        <TrialReminder selected={tierSelected === 'plus'} tier="plus" currencyCode={currencyCode} cost={plans.plus.priceTotal} />
      )}
    </button>
  );

  return (
    <div className={classNames(styles.planSelectionWrapper, className)}>
      {!hideCompareLink && (
        <div className={styles.topRow}>
          <Typography variant="text100bold">
            <FormattedMessage defaultMessage="Choose your plan" />
          </Typography>
          <Link
            size="sm"
            variant="primary"
            onClick={() => {
              scrollToFeatureList();
            }}
            className={styles.compareLink}
          >
            <FormattedMessage defaultMessage="Compare" />
          </Link>
        </div>
      )}
      {showPlusFirst && plusPlanButton}
      <button
        type="button"
        className={classNames(styles.planContainer, { [styles.peakSelected]: tierSelected === 'peak' })}
        onClick={() => {
          setTierSelected('peak');
        }}
      >
        <div className={styles.peakRow}>
          <PeakLogoBadge size="md" variant="full" />
          <span className={styles.badge}>
            <FormattedMessage defaultMessage="Includes Plus benefits" />
          </span>
        </div>
        <Typography variant="text200" className={styles.description}>
          {getPeakText()}
        </Typography>
        <Typography variant="text100">{getPricingText({ plan: plans.peak, currencyCode, isPromoEligible, isMobileSizedScreen })}</Typography>
        {isTrialTimelineTreatmentEnabled && (
          <TrialReminder selected={tierSelected === 'peak'} tier="peak" currencyCode={currencyCode} cost={plans.peak.priceTotal} />
        )}
      </button>
      {!showPlusFirst && plusPlanButton}
    </div>
  );
};
export default PlanSelection;
